import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Available languages - Only English and Turkish
export const languages = [
  { code: 'en', name: 'English', flag: 'us' },
  { code: 'tr', name: 'Türkçe', flag: 'tr' }
];

// Create context
const LanguageContext = createContext();

// Import translations - Only EN and TR
import en from '../locales/en.json';
import tr from '../locales/tr.json';

const translations = { en, tr };

// Provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('kartalair_language');
    // Only allow 'en' or 'tr', default to 'en'
    return saved && (saved === 'en' || saved === 'tr') ? saved : 'en';
  });

  // Save to localStorage when language changes
  useEffect(() => {
    localStorage.setItem('kartalair_language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Get translation function
  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }
    
    // Fallback to English if translation not found
    if (value === undefined) {
      value = translations['en'];
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          value = key; // Return key if not found
          break;
        }
      }
    }
    
    // Replace parameters
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([param, val]) => {
        value = value.replace(new RegExp(`{{${param}}}`, 'g'), val);
      });
    }
    
    return value || key;
  }, [language]);

  const changeLanguage = useCallback((code) => {
    if (code === 'en' || code === 'tr') {
      setLanguage(code);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
